import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";
import { AuthService } from "src/app/services/auth.service";
import { OfficeService } from "src/app/services/office.service";
import { environment } from "src/environments/environment";

interface NavItem {
  label: string;
  icon: string;
  route: string;
}

enum UserRole {
  ADMIN = "ROLE_ADMIN",
  SUPERVISOR = "ROLE_SUPERVISOR",
  MANAGER = "ROLE_MANAGER",
  USER = "ROLE_USER",
}

interface RoleResponse {
  name: string;
}

@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
})
export class NavbarComponent implements OnInit, OnDestroy {
  navItems: NavItem[] = [];

  isAdmin = false;
  isSupervisor = false;
  isManager = false;
  isUser = false;
  isDropdownOpen = false;
  isStaging = !environment.production;

  // Responsividade
  isMobile = false;
  isDrawerOpen = false;

  personName = "";
  personRole = "";
  personPicture = "";
  currentTime = "";
  selectedCompanyName = "";
  selectedCompanyId = "";
  availableCompanies: any[] = [];

  userRoles: RoleResponse[];

  private destroyed$ = new Subject<void>();
  private intervalId: any;

  private readonly roleNavMap: Record<UserRole, NavItem[]> = {
    [UserRole.ADMIN]: [
      { label: "Início", icon: "fas fa-home", route: "/inicio/gerente" },
      { label: "Funcionários", icon: "fas fa-users", route: "/funcionarios" },
      { label: "Profissionais Autônomos", icon: "fas fa-user-tie", route: "/profissionais-autonomos" },
      { label: "Prestadores de Serviço", icon: "fas fa-truck-field", route: "/prestadores-de-servico" },
      { label: "Cargos", icon: "fas fa-suitcase", route: "/responsibilities" },
      { label: "Rotinas", icon: "fas fa-list-check", route: "/routines" },
      { label: "Tarefas", icon: "fas fa-list-check", route: "/tarefas" },
      { label: "Metas", icon: "fas fa-list-check", route: "/goals" },
      { label: "Configurações", icon: "fas fa-user-gear", route: "/configuracoes" },
    ],
    [UserRole.MANAGER]: [
      { label: "Início", icon: "fas fa-home", route: "/inicio/gerente" },
      { label: "Funcionários", icon: "fas fa-users", route: "/funcionarios" },
      { label: "Profissionais Autônomos", icon: "fas fa-user-tie", route: "/profissionais-autonomos" },
      { label: "Prestadores de Serviço", icon: "fas fa-truck-field", route: "/prestadores-de-servico" },
      { label: "Cargos", icon: "fas fa-suitcase", route: "/responsibilities" },
      { label: "Tarefas", icon: "fas fa-list-check", route: "/tasks" },
      { label: "Metas", icon: "fas fa-list-check", route: "/goals" },
      { label: "Documentos", icon: "fas fa-folder-open", route: "/documentos/gerente" },
      { label: "Configurações", icon: "fas fa-user-gear", route: "/configuracoes" },
    ],
    [UserRole.SUPERVISOR]: [
      { label: "Início", icon: "fas fa-home", route: "/inicio/gerente" },
      { label: "Funcionários", icon: "fas fa-users", route: "/funcionarios" },
      { label: "Profissionais Autônomos", icon: "fas fa-user-tie", route: "/profissionais-autonomos" },
      { label: "Prestadores de Serviço", icon: "fas fa-truck-field", route: "/prestadores-de-servico" },
      { label: "Documentos", icon: "fas fa-folder-open", route: "/documentos/gerente" },
      { label: "Configurações", icon: "fas fa-user-gear", route: "/configuracoes" },
    ],
    [UserRole.USER]: [
      { label: "Início", icon: "fas fa-home", route: "/inicio/usuario" },
      { label: "Minhas Rotinas", icon: "fas fa-chart-line", route: "/routines" },
      { label: "Minhas Tarefas", icon: "fas fa-list-check", route: "/tarefas" },
      { label: "Minhas Metas", icon: "fas fa-bullseye", route: "/metas/usuario" },
      { label: "Documentos", icon: "fas fa-folder-open", route: "/documentos/usuario" },
      { label: "Configurações", icon: "fas fa-user-gear", route: "/configuracoes" },
    ],
  };

  constructor(
    private authService: AuthService,
    private officeService: OfficeService,
    private toast: ToastrService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.userRoles = this.loadUserRoles();
    this.personName = this.loadUserName() || "";
    this.personPicture = this.loadUserPhoto() || "";
    this.loadSelectedCompany();
    this.loadAvailableCompanies();

    this.checkPermission();
    this.startClock();

    // Inicializar responsividade
    this.checkScreenSize();
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private loadUserRoles(): RoleResponse[] {
    return this.authService.getUserRoles();
  }

  private loadUserName(): string {
    return this.authService.getCurrentUserName();
  }

  private loadUserPhoto(): string {
    return this.authService.getCurrentUserPhoto();
  }

  private loadSelectedCompany(): void {
    const currentUserString = localStorage.getItem("currentUser") || sessionStorage.getItem("currentUser");
    if (currentUserString) {
      const currentUser = JSON.parse(currentUserString);
      this.selectedCompanyId = currentUser.currentCompanyId;

      if (this.selectedCompanyId) {
        this.officeService.findById(this.selectedCompanyId).subscribe({
          next: (office) => {
            this.selectedCompanyName = office.fantasyName;
          },
          error: (error) => {
            this.selectedCompanyName = "Empresa não encontrada";
          },
        });
      }
    }
  }

  private loadAvailableCompanies(): void {
    this.officeService.findAll().subscribe({
      next: (companies) => {
        this.availableCompanies = companies;
      },
      error: (error) => {},
    });
  }

  selectCompany(companyId: string): void {
    if (companyId === this.selectedCompanyId) {
      return;
    }

    this.authService.selectOffice(companyId).subscribe({
      next: (response) => {
        this.authService.saveToken(response.token);
        this.selectedCompanyId = companyId;

        const selectedCompany = this.availableCompanies.find((company) => company.id === companyId);
        if (selectedCompany) {
          this.selectedCompanyName = selectedCompany.name;
        }

        this.toast.success("Empresa alterada com sucesso!");
        this.redirectToHomePage();
      },
      error: (error) => {},
    });
  }

  private redirectToHomePage(): void {
    const roles = this.authService.getUserRoles();
    if (roles && roles.length > 0) {
      // Verificar se o usuário tem algum role de gerência (prioridade para roles administrativos)
      const hasManagerRole = roles.some(
        (role) => role.name === "ROLE_ADMIN" || role.name === "ROLE_MANAGER" || role.name === "ROLE_SUPERVISOR",
      );

      if (hasManagerRole) {
        this.router.navigate(["/inicio/gerente"]);
      } else {
        this.router.navigate(["/inicio/usuario"]);
      }
    } else {
      // Fallback para usuário se não conseguir determinar o role
      this.router.navigate(["/inicio/usuario"]);
    }
  }

  getMaterialIcon(fontAwesomeIcon: string): string {
    const iconMap: { [key: string]: string } = {
      "fas fa-home": "home",
      "fas fa-users": "people",
      "fas fa-user-tie": "business_center",
      "fas fa-truck-field": "local_shipping",
      "fas fa-suitcase": "work",
      "fas fa-list-check": "task_alt",
      "fas fa-user-gear": "settings",
      "fas fa-chart-line": "trending_up",
      "fas fa-bullseye": "track_changes",
      "fas fa-folder-open": "folder_open",
      "fas fa-chart-pie": "pie_chart",
    };

    return iconMap[fontAwesomeIcon] || "help_outline";
  }

  truncateText(text: string, maxLength: number = 20): string {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  }

  // Métodos de responsividade
  toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
    this.cdr.markForCheck();
  }

  closeDrawer(): void {
    this.isDrawerOpen = false;
    this.cdr.markForCheck();
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth <= 1024;
    this.cdr.markForCheck();
  }

  @HostListener("window:resize", ["$event"])
  onResize(): void {
    this.checkScreenSize();
    if (!this.isMobile) {
      this.closeDrawer();
    }
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event): void {
    if (this.isDrawerOpen && this.isMobile) {
      const target = event.target as HTMLElement;
      if (!target.closest(".sidebar") && !target.closest(".menu-trigger")) {
        this.closeDrawer();
      }
    }
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      if (this.isDrawerOpen) {
        this.closeDrawer();
      }
      if (this.isDropdownOpen) {
        this.isDropdownOpen = false;
        this.cdr.markForCheck();
      }
    }
  }

  logout(): void {
    this.authService.logout();
    this.toast.success("Logout realizado com sucesso", "Logout");
    this.router.navigate(["/sign-in"]);
  }

  private startClock(): void {
    this.updateCurrentTime();
    this.intervalId = setInterval(() => {
      this.updateCurrentTime();
    }, 1000);
  }

  private updateCurrentTime(): void {
    const now = new Date();
    this.currentTime = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }

  private checkPermission(): void {
    this.userRoles.forEach((role) => {
      switch (role.name) {
        case UserRole.ADMIN:
          this.isAdmin = true;
          this.personRole = "Administrador";
          this.generateNavItems(UserRole.ADMIN);
          break;
        case UserRole.SUPERVISOR:
          this.isSupervisor = true;
          this.personRole = "Supervisor";
          this.generateNavItems(UserRole.SUPERVISOR);
          break;
        case UserRole.MANAGER:
          this.isManager = true;
          this.personRole = "Gerente";
          this.generateNavItems(UserRole.MANAGER);
          break;
        case UserRole.USER:
          this.isUser = true;
          this.personRole = "Usuário";
          this.generateNavItems(UserRole.USER);
          break;
      }
    });
  }

  private generateNavItems(role: UserRole): void {
    this.clearNavItems();
    const items = this.roleNavMap[role];
    if (items) {
      this.navItems.push(...items);
    }
  }

  private clearNavItems(): void {
    this.navItems = [];
  }

  private handleErrors(ex: any): void {
    if (ex.error?.errors) {
      ex.error.errors.forEach((element: { message: string }) => {
        this.toast.error(element.message);
      });
    } else {
      this.toast.error(ex.error?.message || "Erro inesperado");
    }
  }
}
